import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {fromJson} from '@github-ui/dotcom-schema/protobuf'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import {queryFnFetchWithProtoSignal} from '@github-ui/react-core/future/query-fn-fetch'
import {getIssuesIndexContentQueryDeps} from '@github-ui/issue-index-query-defaults'
import {getIssueStateCountsQuery} from '@github-ui/issues-shared/issue-state-filter'
import {issuesIndexMainQuery} from '@github-ui/issues-index-query'
import {
  type RepoIssuesDashboardContentResponse,
  RepoIssuesDashboardContentResponseSchema,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'

import {repoAppBuilder} from '@github-ui/repo-app-builder'
import type {IssuesListSecondaryResult} from '../components/IssuesListSurface'
import {repoDeferredToListItem} from '../helpers/repo-issues-deferred'
import {issuesIndexQueryResolver} from '../helpers/issues-index-query-resolver'

export const ISSUES_INDEX_PATHS = {
  index: '/:owner/:repo/issues',
  mentioned: '/:owner/:repo/issues/mentioned/:mentioned',
  assigned: '/:owner/:repo/issues/assigned/:assignee',
  createdBy: '/:owner/:repo/issues/created_by/:author',
  createdByApp: '/:owner/:repo/issues/created_by/app/:author',
  label: '/:owner/:repo/labels/:label',
} as const

export interface IssuesIndexSecondaryDataEnvelope {
  payload: {
    issuesIndexSecondaryData?: unknown
  }
}

export function adaptIssuesIndexSecondaryData(json: IssuesIndexSecondaryDataEnvelope) {
  const rawDeferred = json.payload.issuesIndexSecondaryData
  if (!rawDeferred) return {payload: {issuesIndexSecondaryData: {results: [] as IssuesListSecondaryResult[]}}}

  const response = fromJson({
    schema: RepoIssuesDashboardContentResponseSchema,
    raw: rawDeferred,
    routeId: 'issuesIndexSecondaryData',
    queryName: 'secondary',
  })
  return {
    payload: {
      issuesIndexSecondaryData: {
        results: response.deferred.map(repoDeferredToListItem).filter(item => item !== undefined),
        pageInfo: response.pageInfo,
      },
    },
  }
}

function queryDeps({pathname, searchParams}: {pathname: string; searchParams: URLSearchParams}) {
  return getIssuesIndexContentQueryDeps({
    pathname,
    query: searchParams.get('q'),
    page: searchParams.get('page'),
  })
}

export function createIssuesIndexSecondaryQuery() {
  return {
    queryName: 'secondary' as const,
    queryDeps: ({
      pathname,
      searchParams,
      params,
    }: {
      pathname: string
      searchParams: URLSearchParams
      params: {owner: string; repo: string}
    }) => {
      const q = issuesIndexQueryResolver({pathname, searchParams})
      const countsQuery = getIssueStateCountsQuery(q)
      return {
        pathname: `/${params.owner}/${params.repo}/issues/deferred`,
        searchParams: {
          q,
          page: searchParams.get('page') || '1',
          ...(countsQuery && {counts_q: countsQuery}),
        },
      }
    },
    queryFn: async (queryKey: Parameters<typeof queryFnFetchWithProtoSignal>[0]) => {
      const {json} = await queryFnFetchWithProtoSignal<IssuesIndexSecondaryDataEnvelope>(queryKey)
      return adaptIssuesIndexSecondaryData(json)
    },
  }
}

export function createIssuesIndexMainQuery({payloadRouteId}: {payloadRouteId?: string} = {}) {
  const query = mainQuery<RepoIssuesDashboardContentResponse>({
    schema: RepoIssuesDashboardContentResponseSchema,
    queryDeps,
  })

  if (!payloadRouteId) return query

  return {
    ...query,
    queryFn: (queryKey: Parameters<typeof query.queryFn>[0], options: Parameters<typeof query.queryFn>[1]) =>
      query.queryFn({...queryKey, routeId: payloadRouteId}, options),
  }
}

/**
 * Repo Issues#index content route. Clean paths are preserved so Rails can apply
 * their route parameters and return the matching embedded payload.
 */
export function buildIssuesIndexContentRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('issuesIndexContentRoute', {
    path: ISSUES_INDEX_PATHS.index,
    queries: [issuesIndexMainQuery(), createIssuesIndexSecondaryQuery()],
  })
}

export function buildIssuesLabelContentRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('issuesLabelContentRoute', {
    path: ISSUES_INDEX_PATHS.label,
    queries: [createIssuesIndexMainQuery(), createIssuesIndexSecondaryQuery()],
  })
}

export const issuesIndexContentRoute = buildIssuesIndexContentRoute(repoAppBuilder)
export const issuesLabelContentRoute = buildIssuesLabelContentRoute(repoAppBuilder)
